import {
    DataProvider,
    GetListParams,
    GetListResult,
    GetManyParams,
    GetManyResult,
    GetOneParams,
    GetOneResult,
} from 'react-admin';
import { read_cookie } from 'sfcookies';
import { apiUrl, UseUrlManagerCleanUrl } from '../components/utils/url-manager';
import { addSortToUrl, processFilters } from './provider/defaults';
import { SalesConversation } from '../types/entities';

/**
 * Read-only provider for the Conversations list.
 *
 * **Deliberately not a copy of the 400-line template the other 55 resources use.** A conversation
 * is never created or edited from a list — it is started by the first reply or note and worked in
 * the panel on its own record — so `create`, `update` and `delete` would be dead code, and every
 * one of those copies also swallows a non-2xx and hands react-admin an empty result (HQ-06). The
 * three methods a list actually needs are here and they surface failures.
 */

const headers = (): Headers => new Headers({
    'Content-Type': 'application/json',
    'token': read_cookie('auth'),
    'hq': 'hq',
});

/**
 * `hydra:member` rows carry an `@id` IRI; react-admin wants a plain `id`, which the API already
 * serialises alongside it.
 */
const fetchCollection = async (url: string): Promise<{ data: SalesConversation[], total: number }> => {
    const request: Request = new Request(UseUrlManagerCleanUrl({ url }), { method: 'GET', headers: headers() });
    const response: Response = await fetch(request);
    if (!response.ok) {
        throw new Error('Could not load conversations');
    }
    const body: any = await response.json();

    return {
        data: body?.['hydra:member'] ?? body?.member ?? [],
        total: body?.['hydra:totalItems'] ?? body?.totalItems ?? 0,
    };
};

const salesConversationDataProvider: DataProvider = {
    getList: async (resource: string, params: GetListParams): Promise<GetListResult> => {
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += `?page=${ params.pagination.page }&itemsPerPage=${ params.pagination.perPage }`;
        }
        url = processFilters(url, params);
        url = addSortToUrl(url, params);

        return fetchCollection(url);
    },

    getOne: async (resource: string, params: GetOneParams): Promise<GetOneResult> => {
        const url: string = UseUrlManagerCleanUrl({ url: `${ apiUrl }${ resource }/${ params.id }` });
        const response: Response = await fetch(new Request(url, { method: 'GET', headers: headers() }));
        if (!response.ok) {
            throw new Error('Could not load the conversation');
        }

        return { data: await response.json() };
    },

    getMany: async (resource: string, params: GetManyParams): Promise<GetManyResult> => {
        if (params.ids.length === 0) {
            return { data: [] };
        }
        const query: string = params.ids.map((id): string => `id[]=${ String(id) }`).join('&');
        const { data } = await fetchCollection(`${ apiUrl }${ resource }?${ query }`);

        return { data };
    },

    // A conversation is started by the first reply or note and worked in the panel on its own
    // record, so nothing below is reachable from this resource.
    getManyReference: (): Promise<never> => Promise.reject(new Error('Not supported')),
    create: (): Promise<never> => Promise.reject(new Error('Conversations are started by replying, not created')),
    update: (): Promise<never> => Promise.reject(new Error('Conversations are edited from their record')),
    updateMany: (): Promise<never> => Promise.reject(new Error('Not supported')),
    delete: (): Promise<never> => Promise.reject(new Error('Not supported')),
    deleteMany: (): Promise<never> => Promise.reject(new Error('Not supported')),
};

export default salesConversationDataProvider;
